// src/pages/PendingWMSEntry.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import DateRangeFilter from "../components/common/DateRangeFilter";
import { normalizeDateInput, toDisplayDate, toDisplayDateTime } from "../utils/dataFormatters";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

const DATA_ENDPOINT = "/api/po/pending-wms-entry";

const COLUMN_DEFINITIONS = [
  { key: "poNumber", label: "PO#", width: 140 },
  { key: "custCode", label: "Cust Code", width: 120 },
  { key: "retailerSku", label: "Retailer SKU", width: 160 },
  { key: "productCode", label: "Product Code", width: 160 },
  { key: "itemName", label: "Item Name", width: 220 },
  { key: "whseCode", label: "WHSE Code", width: 140 },
  { key: "qty", label: "Qty", width: 110, type: "number" },
  { key: "mabd", label: "MABD", width: 150, type: "date" },
  { key: "eventCode", label: "Event Code", width: 150 },
  { key: "tscpStatus", label: "TSCP Status", width: 160 },
  { key: "tscpHold", label: "TSCP Hold", width: 150 },
];

const DEFAULT_COLUMN_WIDTHS = COLUMN_DEFINITIONS.reduce((acc, column) => {
  acc[column.key] = column.width || 180;
  return acc;
}, {});

const DATE_COLUMN_KEYS = COLUMN_DEFINITIONS.filter((column) => column.type === "date").map((column) => column.key);
const DATETIME_COLUMN_KEYS = COLUMN_DEFINITIONS.filter((column) => column.type === "datetime").map((column) => column.key);
const DATE_COLUMN_SET = new Set(DATE_COLUMN_KEYS);
const DATETIME_COLUMN_SET = new Set(DATETIME_COLUMN_KEYS);

const DEFAULT_PAGE_OPTIONS = [25, 50, 100, "All"];
const EMPTY_VALUE = "__EMPTY__";
const CONTROL_COLUMN_WIDTH = 52;
const toArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const arraysEqual = (a = [], b = []) => a.length === b.length && a.every((val, idx) => val === b[idx]);
const getDefaultWidth = (key) => DEFAULT_COLUMN_WIDTHS[key] || 180;

const normaliseFilterValue = (value) => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === "number") {
    if (Number.isNaN(value)) return EMPTY_VALUE;
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  const trimmed = String(value).trim();
  return trimmed === "" ? EMPTY_VALUE : trimmed;
};

const displayLabelForToken = (raw, token) => {
  if (token === EMPTY_VALUE) return "(Blank)";
  if (raw === null || raw === undefined) return String(token);
  return String(raw);
};

const formatTimestampForFilename = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
};

const numberLike = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return value;
  const parsed = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const compareForSort = (left, right) => {
  const leftNumber = numberLike(left);
  const rightNumber = numberLike(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }
  const leftString = left === null || left === undefined ? "" : String(left).toLowerCase();
  const rightString = right === null || right === undefined ? "" : String(right).toLowerCase();
  return leftString.localeCompare(rightString);
};

const createInitialFilters = () => {
  const next = {};
  COLUMN_DEFINITIONS.forEach(({ key }) => {
    next[key] = [];
    if (DATE_COLUMN_SET.has(key)) {
      next[`${key}From`] = "";
      next[`${key}To`] = "";
    }
  });
  return next;
};

const createInitialWidths = () =>
  COLUMN_DEFINITIONS.reduce((acc, column) => {
    acc[column.key] = getDefaultWidth(column.key);
    return acc;
  }, {});

// TODO: Transform the API payload to the rows expected by the table.
const buildRowsFromPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (payload.rows && Array.isArray(payload.rows)) return payload.rows;
  return [];
};

const transformRowsForDisplay = (rows = []) =>
  rows.map((row = {}) => {
    const next = { ...row };
    DATE_COLUMN_KEYS.forEach((key) => {
      const raw = row?.[key];
      const iso = normalizeDateInput(raw, false) || "";
      next[`${key}Raw`] = iso;
      next[key] = toDisplayDate(iso || raw);
    });
    DATETIME_COLUMN_KEYS.forEach((key) => {
      const raw = row?.[key];
      const iso = normalizeDateInput(raw, true) || "";
      next[`${key}Raw`] = iso;
      next[key] = toDisplayDateTime(iso || raw);
    });
    return next;
  });

export default function PendingWMSEntry() {
  const [rows, setRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(COLUMN_DEFINITIONS[0]?.key || "");
  const [sortDirection, setSortDirection] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [visibleColumns, setVisibleColumns] = useState(() => COLUMN_DEFINITIONS.map(({ key }) => key));
  const [widths, setWidths] = useState(() => createInitialWidths());
  const columnOptions = useMemo(() => COLUMN_DEFINITIONS.map(({ key, label }) => ({ value: key, label })), []);
  const dragRef = useRef({ key: "", startX: 0, startW: 0 });
  const me = getCurrentUser();
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(me?.role, "pending-wms-entry");
  const navigate = useNavigate();

  const fetchRows = useCallback(async () => {
    if (permissionLevel === "None") {
      setRows([]);
      setWarnings([]);
      setLoading(false);
      setError("");
      setLastUpdated(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const headers = {};
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role || "";
      const response = await fetch(`${API_BASE}${DATA_ENDPOINT}`, { headers });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to load Pending WMS Entry data.");
      }
      const payload = await response.json().catch(() => ({}));
      if (payload?.ok !== true) {
        throw new Error(payload?.error || "Failed to load Pending WMS Entry data.");
      }
      setWarnings(Array.isArray(payload?.warnings) ? payload.warnings : []);
      const tableRows = buildRowsFromPayload(payload);
      const preparedRows = transformRowsForDisplay(tableRows || []);
      setRows(preparedRows);
      setLastUpdated(new Date());
      setPage(1);
    } catch (err) {
      setRows([]);
      setWarnings([]);
      setError(err.message || "Failed to load Pending WMS Entry data.");
      setLastUpdated(null);
    } finally {
      setLoading(false);
    }
  }, [permissionLevel, me?.email, me?.role, setPage]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const filterOptions = useMemo(() => {
    const map = {};
    const seen = {};
    COLUMN_DEFINITIONS.forEach(({ key }) => {
      map[key] = [];
      seen[key] = new Set();
    });

  rows.forEach((row) => {
    COLUMN_DEFINITIONS.forEach(({ key }) => {
      if (DATE_COLUMN_SET.has(key)) return;
      const raw = row?.[key];
      const token = normaliseFilterValue(raw);
      if (seen[key].has(token)) return;
      seen[key].add(token);
      map[key].push({ value: token, label: displayLabelForToken(raw, token) });
      });
    });

    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => a.label.localeCompare(b.label));
    });
    return map;
  }, [rows]);

  const displayColumns = useMemo(
    () => COLUMN_DEFINITIONS.filter(({ key }) => visibleColumns.includes(key)),
    [visibleColumns]
  );

  const onVisibleColumnsChange = useCallback(
    (next) => {
      const list = toArray(next);
      if (!list.length) return;
      const ordered = COLUMN_DEFINITIONS.map(({ key }) => key).filter((key) => list.includes(key));
      setVisibleColumns(ordered);
      if (sortKey && !ordered.includes(sortKey)) {
        setSortKey("");
        setSortDirection(0);
      }
    },
    [sortKey]
  );

  const onDragging = useCallback((event) => {
    const { key, startX, startW } = dragRef.current;
    if (!key) return;
    const dx = event.clientX - startX;
    const nextWidth = Math.max(80, startW + dx);
    setWidths((prev) => {
      if (prev[key] === nextWidth) return prev;
      return { ...prev, [key]: nextWidth };
    });
  }, []);

  const onDragEnd = useCallback(() => {
    window.removeEventListener("mousemove", onDragging);
    window.removeEventListener("mouseup", onDragEnd);
    dragRef.current = { key: "", startX: 0, startW: 0 };
  }, [onDragging]);

  const onDragStart = (event, key) => {
    dragRef.current = { key, startX: event.clientX, startW: widths[key] || getDefaultWidth(key) };
    window.addEventListener("mousemove", onDragging);
    window.addEventListener("mouseup", onDragEnd);
    event.preventDefault();
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onDragging);
      window.removeEventListener("mouseup", onDragEnd);
    };
  }, [onDragging, onDragEnd]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const current = prev[key];
      if (Array.isArray(value)) {
        const currentArray = Array.isArray(current) ? current : [];
        if (arraysEqual(currentArray, value)) return prev;
        return { ...prev, [key]: value };
      }
      if (current === value) return prev;
      return { ...prev, [key]: value };
    });
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setPage(1);
  }, [setPage]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const activeValueFilters = COLUMN_DEFINITIONS.filter(
      ({ key }) => !DATE_COLUMN_SET.has(key) && (filters[key] || []).length > 0
    );
    return rows.filter((row) => {
      for (const { key } of activeValueFilters) {
        const selected = filters[key] || [];
        if (!selected.length) continue;
        const token = normaliseFilterValue(row?.[key]);
        if (!selected.includes(token)) return false;
      }
      for (const key of DATE_COLUMN_KEYS) {
        const from = filters[`${key}From`] || "";
        const to = filters[`${key}To`] || "";
        if (!from && !to) continue;
        const rawValue = row?.[`${key}Raw`] || row?.[key] || "";
        if (!rawValue) return false;
        const valueTime = new Date(rawValue).getTime();
        if (Number.isNaN(valueTime)) return false;
        if (from) {
          const fromTime = new Date(from).getTime();
          if (!Number.isNaN(fromTime) && valueTime < fromTime) return false;
        }
        if (to) {
          const toTime = new Date(to).getTime();
          if (!Number.isNaN(toTime) && valueTime > toTime) return false;
        }
      }
      if (!term) return true;
      return COLUMN_DEFINITIONS.some(({ key }) => {
        const value = row?.[key];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [rows, filters, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortKey || sortDirection === 0) return filteredRows;
    const next = [...filteredRows];
    next.sort((a, b) => {
      const left = DATE_COLUMN_SET.has(sortKey) || DATETIME_COLUMN_SET.has(sortKey)
        ? a?.[`${sortKey}Raw`] || a?.[sortKey]
        : a?.[sortKey];
      const right = DATE_COLUMN_SET.has(sortKey) || DATETIME_COLUMN_SET.has(sortKey)
        ? b?.[`${sortKey}Raw`] || b?.[sortKey]
        : b?.[sortKey];
      const base = compareForSort(left, right);
      return sortDirection === -1 ? -base : base;
    });
    return next;
  }, [filteredRows, sortKey, sortDirection]);

  const totalRows = filteredRows.length;
  const totalPages = useMemo(() => {
    if (!totalRows) return 1;
    const size = pageSize === "All" ? totalRows || 1 : pageSize;
    return Math.max(1, Math.ceil(totalRows / size));
  }, [totalRows, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    if (pageSize === "All") return sortedRows;
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const startIdx = totalRows === 0 ? 0 : pageSize === "All" ? 0 : (page - 1) * pageSize;
  const endIdx =
    totalRows === 0
      ? 0
      : pageSize === "All"
      ? totalRows
      : Math.min(totalRows, startIdx + (typeof pageSize === "number" ? pageSize : totalRows));

  const toggleSort = (key) => {
    if (sortKey !== key || !sortKey) {
      setSortKey(key);
      setSortDirection(1);
      return;
    }
    const nextDirection = sortDirection === 1 ? -1 : sortDirection === -1 ? 0 : 1;
    if (nextDirection === 0) {
      setSortDirection(0);
      setSortKey("");
    } else {
      setSortDirection(nextDirection);
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key || sortDirection === 0) {
      return (
        <svg className="h-3.5 w-3.5 opacity-40" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 7l3-3 3 3H7zM13 13l-3 3-3-3h6z" />
        </svg>
      );
    }
    if (sortDirection === 1) {
      return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 11l3 3 3-3H7z" />
        </svg>
      );
    }
    return (
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
        <path d="M7 9l3-3 3 3H7z" />
      </svg>
    );
  };

  const onExport = useCallback(() => {
    if (!sortedRows.length) return;
    const header = COLUMN_DEFINITIONS.map((column) => column.label);
    const dataRows = sortedRows.map((row) =>
      COLUMN_DEFINITIONS.map((column) => {
        const value = row?.[column.key];
        return value === null || value === undefined ? "" : value;
      })
    );
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pending WMS Entry");
    const filename = `Pending_WMS_Entry_${formatTimestampForFilename(new Date())}.xlsx`;
    XLSX.writeFile(workbook, filename);
  }, [sortedRows]);

  const onPivot = useCallback(() => {
    if (!sortedRows.length) return;
    navigate("/pos/pending-wms-entry/pivot", { state: { rows: sortedRows } });
  }, [navigate, sortedRows]);

  const exportDisabled = sortedRows.length === 0;

  if (!COLUMN_DEFINITIONS.length) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        Populate COLUMN_DEFINITIONS before rendering PendingWMSEntry.
      </div>
    );
  }

  if (permissionLevel === "None") {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        You do not have access to Pending WMS Entry.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Pending WMS Entry</h1>
            <p className="text-sm text-slate-600">PO lines without WMS outbound codes.</p>
          </div>
          <span className="text-sm text-slate-600 whitespace-nowrap">
            Filtered {totalRows.toLocaleString()} rows
          </span>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search all columns…"
              className="w-64 rounded border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={onExport}
              disabled={exportDisabled}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg className="h-4 w-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13 7V3H7v4H4l6 6 6-6h-3z" />
                <path d="M4 15h12v2H4z" />
              </svg>
              Export
            </button>
            <button
              type="button"
              onClick={fetchRows}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-40"
              disabled={loading}
            >
              <svg
                className={`h-4 w-4 ${loading ? "animate-spin text-blue-500" : "text-slate-500"}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4 10a6 6 0 0110.39-4.19l1.42-1.42A8 8 0 102 10h2zm12-4h-4v2h4V6z"
                  clipRule="evenodd"
                />
              </svg>
              Refresh
            </button>
            <button
              type="button"
              onClick={onPivot}
              disabled={loading || sortedRows.length === 0}
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Pivot
            </button>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 text-sm text-slate-500">
          <div>
            {lastUpdated
              ? `Last updated ${lastUpdated.toLocaleString()}`
              : loading
              ? "Loading..."
              : "Awaiting data"}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <MultiSelectDropdown
              value={visibleColumns}
              options={columnOptions}
              onChange={onVisibleColumnsChange}
              placeholder="Columns"
              buttonClassName="text-xs min-w-[150px]"
            />
            <span>Rows:</span>
            <select
              className="border rounded px-2 py-1 text-sm"
              value={pageSize}
              onChange={(event) => {
                const value = event.target.value;
                setPageSize(value === "All" ? "All" : parseInt(value, 10));
                setPage(1);
              }}
            >
              {DEFAULT_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              {"<"}
            </button>
            <span className="tabular-nums">{page} / {totalPages}</span>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
              aria-label="Next page"
            >
              {">"}
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              aria-label="Last page"
            >
              {">>"}
            </button>
            <span className="text-slate-500 tabular-nums">
              {totalRows ? `${startIdx + 1} - ${endIdx} of ${totalRows}` : "0"}
            </span>
          </div>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <div className="font-medium">Some rows could not resolve WHSE Code from Ship Point Management.</div>
          <div className="mt-1">Review Ship Point Management for the affected customer and ship point combinations.</div>
          <div className="mt-2 space-y-1">
            {warnings.slice(0, 5).map((warning, index) => (
              <div key={`${warning?.poNumber || ""}-${warning?.custCode || ""}-${warning?.shipPoint || ""}-${index}`}>
                {warning?.message ||
                  `No warehouse mapping found for Cust Code ${warning?.custCode || ""} and Ship Point ${warning?.shipPoint || ""}.`}
              </div>
            ))}
            {warnings.length > 5 ? <div>Showing 5 of {warnings.length} warnings.</div> : null}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="relative max-h-[calc(100vh-140px)] overflow-auto rounded border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th
                className="sticky top-0 z-20 bg-white border-b px-3 py-2 text-left text-sm font-semibold"
                style={{ width: CONTROL_COLUMN_WIDTH, minWidth: CONTROL_COLUMN_WIDTH }}
              />
              {displayColumns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: widths[column.key], minWidth: widths[column.key] }}
                  className="sticky top-0 z-20 bg-white border-b px-3 py-2 text-left text-sm font-semibold relative select-none"
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className="flex items-center gap-1 pr-3 text-left"
                  >
                    <span>{column.label}</span>
                    {sortIcon(column.key)}
                  </button>
                  <span
                    onMouseDown={(event) => onDragStart(event, column.key)}
                    className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                    title="Drag to resize"
                    style={{ userSelect: "none" }}
                  />
                </th>
              ))}
            </tr>
            <tr className="sticky top-[42px] z-10 bg-white">
              <th
                className="bg-emerald-50 border-b px-2 py-1"
                style={{ width: CONTROL_COLUMN_WIDTH, minWidth: CONTROL_COLUMN_WIDTH }}
              >
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={resetFilters}
                    title="Reset filters"
                    aria-label="Reset filters"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12a9 9 0 1 1-3-6.7L21 7" />
                      <path d="M21 3v4h-4" />
                    </svg>
                  </button>
                </div>
              </th>
              {displayColumns.map((column, index) => {
                const options = filterOptions[column.key] || [];
                const control = DATE_COLUMN_SET.has(column.key) ? (
                  <DateRangeFilter
                    value={{
                      from: filters[`${column.key}From`] || "",
                      to: filters[`${column.key}To`] || "",
                    }}
                    onChange={({ from, to }) => {
                      setFilter(`${column.key}From`, from || "");
                      setFilter(`${column.key}To`, to || "");
                    }}
                    placeholder="From-To"
                  />
                ) : (
                  <MultiSelectDropdown
                    value={filters[column.key]}
                    options={options}
                    onChange={(next) => setFilter(column.key, next)}
                    placeholder="All"
                    buttonClassName="text-xs"
                  />
                );
                return (
                  <th
                    key={column.key}
                    className="bg-emerald-50 border-b px-2 py-1"
                    style={{ width: widths[column.key], minWidth: widths[column.key] }}
                  >
                    {control}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={displayColumns.length + 1}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  Loading data…
                </td>
              </tr>
            ) : paginatedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={displayColumns.length + 1}
                  className="px-3 py-6 text-center text-sm text-slate-500"
                >
                  No records match the current filters.
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b">
                  <td
                    className="px-3 py-2"
                    style={{ width: CONTROL_COLUMN_WIDTH, minWidth: CONTROL_COLUMN_WIDTH }}
                  />
                  {displayColumns.map((column) => {
                    const value = row?.[column.key];
                    return (
                      <td
                        key={column.key}
                        className="px-3 py-2 text-sm text-slate-700"
                        style={{ width: widths[column.key], minWidth: widths[column.key] }}
                      >
                        {value === null || value === undefined || value === "" ? "\u2014" : value}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MultiSelectDropdown({
  value = [],
  options = [],
  onChange,
  placeholder = "All",
  buttonClassName = "",
}) {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(0);
  const selectedValues = toArray(value);
  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      if (!option) return;
      const val = option.value;
      if (val === undefined || val === null) return;
      map.set(val, option.label ?? option.value ?? "");
    });
    return map;
  }, [options]);

  useEffect(() => {
    const handleClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const btn = buttonRef.current;
    if (!btn) return;
    const measure = () => {
      const { width } = btn.getBoundingClientRect();
      if (width && Math.abs(width - menuWidth) > 0.5) {
        setMenuWidth(width);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, menuWidth]);

  const optionOrder = useMemo(() => options.map((option) => option.value), [options]);
  const buildNextList = (nextSet) => {
    const ordered = optionOrder.filter((item) => nextSet.has(item));
    selectedValues.forEach((item) => {
      if (!nextSet.has(item)) return;
      if (optionOrder.includes(item)) return;
      ordered.push(item);
    });
    return ordered;
  };

  const toggleOption = (optionValue) => {
    const nextSet = new Set(selectedValues);
    if (nextSet.has(optionValue)) nextSet.delete(optionValue);
    else nextSet.add(optionValue);
    const nextList = buildNextList(nextSet);
    onChange?.(nextList);
  };

  const clearSelection = () => {
    if (selectedValues.length === 0) return;
    onChange?.([]);
  };

  const labels = selectedValues.map((val) => labelMap.get(val) || String(val || ""));
  let summary = placeholder;
  if (labels.length === 1) summary = labels[0] || placeholder;
  else if (labels.length === 2) summary = labels.join(", ");
  else if (labels.length > 2) summary = `${labels.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`flex w-full items-center justify-between gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${buttonClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-slate-700">{summary || placeholder}</span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 z-20 mt-1 rounded-md border border-slate-200 bg-white shadow-lg"
          style={{
            minWidth: menuWidth || undefined,
            width: menuWidth || undefined,
          }}
        >
          <div className="max-h-60 overflow-y-auto py-1">
            {options.length ? (
              options.map((option) => {
                const val = option.value;
                const label = option.label ?? option.value ?? "";
                const checked = selectedValues.includes(val);
                return (
                  <label
                    key={val}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-blue-600"
                      checked={checked}
                      onChange={() => toggleOption(val)}
                    />
                    <span className="truncate">{label || "(Blank)"}</span>
                  </label>
                );
              })
            ) : (
              <div className="px-3 py-2 text-xs text-slate-500">No options available</div>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 px-3 py-1.5 text-xs">
            <button
              type="button"
              onClick={clearSelection}
              className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              disabled={selectedValues.length === 0}
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-blue-600 hover:underline"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
